/**
 * Minimal Markdown-to-HTML renderer.
 * Handles the subset of Markdown Gemini typically produces in hints:
 * headings, bold, italic, inline code, code blocks, links, lists, paragraphs.
 *
 * We intentionally keep this tiny instead of pulling in a full library.
 */

import Prism from 'prismjs';
import 'prismjs/components/prism-bash';
import 'prismjs/components/prism-css';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-json';
import 'prismjs/components/prism-jsx';
import 'prismjs/components/prism-markup';
import 'prismjs/components/prism-python';

/**
 * Convert a Markdown string to an HTML string.
 * @param {string} md
 * @returns {string}
 */
export function renderMarkdown(md) {
  if (!md) return '';

  let html = md;
  const codeBlocks = [];

  // ---- Code blocks (```lang ... ```) ----
  html = html.replace(/```([^\n`]*)\n?([\s\S]*?)```/g, (_match, lang, code) => {
    const language = normalizeLanguage(lang);
    const highlightedCode = highlightCode(code.trimEnd(), language);
    const languageClass = language
      ? ` class="language-${escapeAttribute(language)}"`
      : '';
    const index = codeBlocks.length;
    codeBlocks.push(`<pre><code${languageClass}>${highlightedCode}</code></pre>`);
    return `\n@@CHATBOT_CODE_BLOCK_${index}@@\n`;
  });

  // ---- Inline code ----
  html = html.replace(
    /`([^`\n]+)`/g,
    (_match, code) => `<code>${escapeHtml(code)}</code>`
  );

  // ---- Headings ----
  html = html.replace(/^#### (.+)$/gm, '<strong>$1</strong>');
  html = html.replace(/^### (.+)$/gm, '<strong>$1</strong>');
  html = html.replace(/^## (.+)$/gm, '<strong>$1</strong>');
  html = html.replace(/^# (.+)$/gm, '<strong>$1</strong>');

  // ---- Bold / italic ----
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');

  // ---- Links [text](url) ----
  html = html.replace(
    /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
  );

  // ---- Lists ----
  html = renderLists(html);

  // ---- Line breaks into paragraphs ----
  html = html
    .split(/\n{2,}/)
    .map(block => {
      const trimmed = block.trim();
      if (!trimmed) return '';
      if (
        trimmed.startsWith('<pre') ||
        trimmed.startsWith('<ul') ||
        trimmed.startsWith('<ol') ||
        trimmed.startsWith('<li') ||
        trimmed.startsWith('@@CHATBOT_CODE_BLOCK_')
      ) {
        return trimmed;
      }
      return `<p>${trimmed.replace(/\n/g, '<br/>')}</p>`;
    })
    .join('\n');

  html = html.replace(/@@CHATBOT_CODE_BLOCK_(\d+)@@/g, (_match, index) => {
    return codeBlocks[Number(index)] || '';
  });

  return html;
}

function escapeHtml(str) {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function escapeAttribute(str) {
  return escapeHtml(str).replace(/"/g, '&quot;');
}

function normalizeLanguage(lang) {
  const normalized = (lang || '').trim().toLowerCase();
  const aliases = {
    html: 'markup',
    py: 'python',
    sh: 'bash',
    shell: 'bash',
    js: 'javascript',
    jsonc: 'json'
  };

  return aliases[normalized] || normalized;
}

function highlightCode(code, language) {
  const grammar = Prism.languages[language];

  if (!grammar) {
    return escapeHtml(code);
  }

  return Prism.highlight(code, grammar, language);
}

function renderLists(html) {
  const lines = html.split('\n');
  const rendered = [];

  for (let i = 0; i < lines.length; i++) {
    const unorderedMatch = lines[i].match(/^\s*[*-]\s+(.+)$/);
    if (unorderedMatch) {
      const items = [];
      while (i < lines.length) {
        const match = lines[i].match(/^\s*[*-]\s+(.+)$/);
        if (!match) break;
        items.push(`<li>${match[1]}</li>`);
        i++;
      }
      i--;
      rendered.push(`<ul>${items.join('')}</ul>`);
      continue;
    }

    const orderedMatch = lines[i].match(/^\s*\d+\.\s+(.+)$/);
    if (orderedMatch) {
      const items = [];
      while (i < lines.length) {
        const match = lines[i].match(/^\s*\d+\.\s+(.+)$/);
        if (!match) break;
        items.push(`<li>${match[1]}</li>`);
        i++;
      }
      i--;
      rendered.push(`<ol>${items.join('')}</ol>`);
      continue;
    }

    rendered.push(lines[i]);
  }

  return rendered.join('\n');
}
